// WS-28: "glass kota" unit builder (resort-agnostic). A Lapland glass-roof cabin: a convex, chamfered plan
// (octagon-like front, long sides, chamfered rear), low walls on a plinth, ONE continuous hipped roof whose front
// facets are glazed (a glass half-pyramid), an optional gabled entrance vestibule at the narrow end, and a snow
// layer that is a separate part (season switch: params.snow = false draws the bare roof).
//
// Why a dedicated builder (round-5 feedback "holes, overlapping roof planes, z-fighting seen top-down"):
// the roof is generated as a single watertight surface from the plan alone. Its height is a function of the
// distance to the eave line, h(p) = eaveY + f(d(p)) (f = band pitch, then main pitch), so every facet is the
// region of the plan closest to one eave edge (the straight skeleton of a convex polygon). Facets therefore meet
// exactly at hips and the ridge: no gaps, no overlapping planes, no coplanar faces. Glass panes, the eave band,
// the opaque cap and the snow are cut from the same facets along level lines of d, so they share edges too.
//
// Local frame (units.js convention): +x = wide end (glass front), narrow end = entrance (-x), +z = left for a
// viewer facing the front, y = 0 at the outside ground. Sizes are absolute metres from params (never stretched
// to the footprint; the survey sets position and orientation).
//
// resort.json: units.<type> = { model: "glassKota", params: { plan, glassFacets, eaveY, ridgeY, band, bandPitch, ... } }
//   plan        half outline of the EAVE (roof edge) on the +z side, front -> rear, convex, mirrored to -z.
//   glassFacets roof facets glazed on each side of the front facet (the front facet is always glass).
//   glassTopFrac where the glass panes stop, as a share of the slope height from the eave band top to the ridge
//               (measure it on a side-on photo). Above it a shingled cap closes the cone: the panes are trapezoids.
//   glassApex   true: the glazed facets all meet in one apex on the ridge line at ridgeY (equal panes, as built).
//               false (older default): the glass pitch comes from the whole plan, so the outer panes are larger.
//   fascia / fasciaMat  height of the eave edge board and its material key (default the band's metal).
//   capRound    true: in front of the ridge middle the cap's level lines round off step by step (`capRings`,
//               `capSegments`) so the roof wraps round the faceted front; behind it the hips stay straight into
//               the gable. false: plain hips up to the ridge.
//   band/bandPitch  the flatter metal skirt, ONLY on the glazed facets; shingled facets are one plane to the eave.
//   entrance    gabled vestibule at the rear: { rearX, frontX, wallHalf, roofHalf, eaveY, ridgeY, overhang, ... }
//               door { w, h, mat }, sign, lamps [[z, y, w, h]] (wall lights), led { r, inset, endGap, wash } (warm LED
//               strip under both sloped rakes of the door-end overhang, 'led' material; `wash` = height in m of a soft
//               additive light wash on the gable face below it, visible only at night).
//   vents       roof pipes [{ x, z, r, top, cap, enabled }] (photos only; vents give no smoke). enabled: false keeps
//               an unconfirmed pipe in the data but leaves it out of the model.
//   snow        { depth, overhang, droop } for the winter layer; the engine passes snow:false in summer.
//   insideModel true: leave out the placeholder room (floor, wall ring, ceilings, bed); a modelled room is drawn
//               there (WS-61 walk-in interiors, units.<type>.walkIn).
import * as THREE from 'three/webgpu';
import { stretchDist } from './lines/measure.js'; // VIS-219: the distance to a stretch, the shared sum

const EPS = 1e-7;
const D2R = Math.PI / 180;
const k3 = (p) => `${p[0].toFixed(4)},${p[1].toFixed(4)},${p[2].toFixed(4)}`;

/** Convex polygon clip, keeps nx*x + nz*z + c >= 0 (Sutherland-Hodgman). */
function clip(poly, nx, nz, c) {
  const out = [];
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const fa = nx * a[0] + nz * a[1] + c, fb = nx * b[0] + nz * b[1] + c;
    if (fa >= -EPS) out.push(a);
    if ((fa > EPS && fb < -EPS) || (fa < -EPS && fb > EPS)) {
      const t = fa / (fa - fb);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  const clean = [];
  for (const p of out) if (!clean.length || Math.hypot(p[0] - clean[clean.length - 1][0], p[1] - clean[clean.length - 1][1]) > 1e-5) clean.push(p);
  while (clean.length > 1 && Math.hypot(clean[0][0] - clean[clean.length - 1][0], clean[0][1] - clean[clean.length - 1][1]) <= 1e-5) clean.pop();
  return clean.length >= 3 && Math.abs(area2(clean)) > 1e-6 ? clean : null;
}
const area2 = (q) => q.reduce((s, a, i) => { const b = q[(i + 1) % q.length]; return s + a[0] * b[1] - b[0] * a[1]; }, 0);

/** Offset a convex polygon by d (positive = inwards), keeping vertex correspondence. */
function offset(poly, d) {
  const s = area2(poly) > 0 ? 1 : -1, n = poly.length;
  const L = poly.map((a, i) => {
    const b = poly[(i + 1) % n], dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz);
    const nx = (-dz / len) * s, nz = (dx / len) * s;
    return { p: [a[0] + nx * d, a[1] + nz * d], d: [dx / len, dz / len] };
  });
  return L.map((l1, i) => {
    const l0 = L[(i - 1 + n) % n], den = l0.d[0] * l1.d[1] - l0.d[1] * l1.d[0];
    if (Math.abs(den) < 1e-9) return l1.p;
    const t = ((l1.p[0] - l0.p[0]) * l1.d[1] - (l1.p[1] - l0.p[1]) * l1.d[0]) / den;
    return [l0.p[0] + l0.d[0] * t, l0.p[1] + l0.d[1] * t];
  });
}

/** Distance from a plan point to a convex polygon (0 inside). */
function distConvex(q, Q) {
  const s = area2(Q) > 0 ? 1 : -1;
  let inside = true, best = Infinity;
  for (let i = 0; i < Q.length; i++) {
    const a = Q[i], b = Q[(i + 1) % Q.length], dx = b[0] - a[0], dz = b[1] - a[1];
    if ((dx * (q[1] - a[1]) - dz * (q[0] - a[0])) * s < 0) inside = false;
    best = Math.min(best, stretchDist(q[0], q[1], a[0], a[1], b[0], b[1]));
  }
  return inside ? 0 : best;
}

/** Triangle soup per material, then one non-indexed BufferGeometry per material (bake() batches them). */
class Soup {
  constructor() { this.parts = new Map(); this.sm = new Map(); }
  _get(mat, grp) {
    const m = grp === 'smooth' ? this.sm : this.parts;
    if (!m.has(mat)) m.set(mat, { pos: [], uv: [] });
    return m.get(mat);
  }
  /** Convex polygon (3D points), fan-triangulated, wound so its normal points along `want` (a vector or 'up'). */
  poly(mat, pts, want = 'up', uvf = null, grp = null) { // grp 'smooth': welded, smooth normals (round cap)
    if (!pts || pts.length < 3) return;
    const n = newell(pts);
    const w = want === 'up' ? [0, 1, 0] : want;
    const flip = n[0] * w[0] + n[1] * w[1] + n[2] * w[2] < 0;
    const P = flip ? [...pts].reverse() : pts;
    const part = this._get(mat, grp);
    for (let i = 1; i < P.length - 1; i++) for (const q of [P[0], P[i], P[i + 1]]) {
      part.pos.push(q[0], q[1], q[2]);
      const uv = uvf ? uvf(q) : null;
      part.uv.push(...(uv || [0, 0]));
    }
    if (!uvf) part.needsBoxUV = true;
  }
  quad(mat, a, b, c, d, want, uvf) { this.poly(mat, [a, b, c, d], want, uvf); }
  add(mat, geo) { // an existing three geometry (rods, boxes)
    const g = geo.index ? geo.toNonIndexed() : geo;
    const part = this._get(mat);
    const p = g.attributes.position.array;
    for (let i = 0; i < p.length; i++) part.pos.push(p[i]);
    for (let i = 0; i < p.length / 3; i++) part.uv.push(0, 0);
    part.needsBoxUV = true;
  }
  build({ smooth = new Set() } = {}) {
    const group = new THREE.Group();
    for (const [mat, part, sm] of [...[...this.parts].map(([m, q]) => [m, q, false]), ...[...this.sm].map(([m, q]) => [m, q, true])]) {
      let g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(part.pos, 3));
      g.setAttribute('uv', new THREE.Float32BufferAttribute(part.uv, 2));
      if (sm || smooth.has(mat)) {
        g = weld(g); g.computeVertexNormals(); g = g.toNonIndexed();
      } else g.computeVertexNormals();
      if (part.needsBoxUV) boxUV(g);
      const mesh = new THREE.Mesh(g, mat);
      mesh.castShadow = !mat.transparent;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    return group;
  }
}
function newell(p) {
  let x = 0, y = 0, z = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i], b = p[(i + 1) % p.length];
    x += (a[1] - b[1]) * (a[2] + b[2]); y += (a[2] - b[2]) * (a[0] + b[0]); z += (a[0] - b[0]) * (a[1] + b[1]);
  }
  return [x, y, z];
}
/** Merge equal positions (smooth snow normals across facets and edges). */
function weld(g) {
  const p = g.attributes.position.array, uv = g.attributes.uv.array;
  const map = new Map(), pos = [], uvs = [], idx = [];
  for (let i = 0; i < p.length / 3; i++) {
    const key = k3([p[i * 3], p[i * 3 + 1], p[i * 3 + 2]]);
    let j = map.get(key);
    if (j === undefined) { j = pos.length / 3; map.set(key, j); pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]); uvs.push(uv[i * 2], uv[i * 2 + 1]); }
    idx.push(j);
  }
  const w = new THREE.BufferGeometry();
  w.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  w.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  w.setIndex(idx);
  return w;
}
/** Box-projected UVs in metres/2 (units.js convention), where a part has no explicit mapping. */
function boxUV(g) {
  const p = g.attributes.position, n = g.attributes.normal, uv = g.attributes.uv;
  for (let i = 0; i < p.count; i++) {
    if (uv.getX(i) !== 0 || uv.getY(i) !== 0) continue;
    const ax = Math.abs(n.getX(i)), ay = Math.abs(n.getY(i)), az = Math.abs(n.getZ(i));
    let u, v;
    if (ay > ax && ay > az) { u = p.getX(i); v = p.getZ(i); } else if (ax > az) { u = p.getZ(i); v = p.getY(i); } else { u = p.getX(i); v = p.getY(i); }
    uv.setXY(i, u * 0.5 + 1e-4, v * 0.5 + 1e-4);
  }
}
function rodGeo(a, b, r, seg = 4) {
  const A = new THREE.Vector3(...a), B = new THREE.Vector3(...b), len = A.distanceTo(B);
  const g = new THREE.CylinderGeometry(r, r, len, seg, 1, false).translate(0, len / 2, 0);
  g.rotateY(Math.PI / seg); // flat face up on 4-sided frame bars
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), B.clone().sub(A).normalize()));
  return g.translate(A.x, A.y, A.z);
}
const boxGeo = (x, y, z, sx, sy, sz, ry = 0) => new THREE.BoxGeometry(sx, sy, sz).rotateY(ry).translate(x, y, z);
/** Additive warm gradient for an LED's light wash (one shared material per library; brightness follows M.led's
 *  night ramp, so it is invisible by day). Texture v = 0 at the light, 1 where the wash fades out. */
const washMats = new WeakMap();
function ledWash(M) {
  if (washMats.has(M)) return washMats.get(M);
  const c = document.createElement('canvas'); c.width = 4; c.height = 64;
  const g = c.getContext('2d'), gr = g.createLinearGradient(0, 0, 0, 64);
  gr.addColorStop(0, '#fff'); gr.addColorStop(0.35, '#555'); gr.addColorStop(1, '#000');
  g.fillStyle = gr; g.fillRect(0, 0, 4, 64);
  const tex = new THREE.CanvasTexture(c); tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
  const mat = new THREE.MeshBasicNodeMaterial({ map: tex, color: 0xffa860, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending });
  const led = M.led || M.lamp;
  Object.defineProperty(mat, 'opacity', { get: () => 0.55 * Math.min(1, Math.max(0, (led.emissiveIntensity - 0.4) / 4)), set() {} });
  Object.defineProperty(mat, 'visible', { get: () => mat.opacity > 0, set() {} }); // WS-75 perf: by day it adds nothing: out of the draw list (one render object less per pass)
  washMats.set(M, mat);
  return mat;
}

/** WS-61: the circle (centre [x, 0], radius r) the glazed eave edges best touch: nx_i x + c_i = r, least squares. */
function glassApexFit(G) {
  let n = 0, sx = 0, sxx = 0, sc = 0, sxc = 0;
  for (const e of G) { n++; sx += e.nx; sxx += e.nx * e.nx; sc += e.c; sxc += e.nx * e.c; }
  const det = sx * sx - n * sxx;
  if (Math.abs(det) < 1e-9) return { x: 0, r: sc / n };
  return { x: (n * sxc - sx * sc) / det, r: (sx * sxc - sxx * sc) / det };
}

export const KOTA_DEFAULTS = {
  plan: [[4.1, 0.85], [3.1, 2.25], [1.5, 2.75], [-1, 2.75], [-2.55, 2.25], [-3.3, 1.25]],
  glassFacets: 2, eaveY: 1.25, ridgeY: 4.05, band: 0.5, bandPitch: 30, glassCap: 0.3, glassTopFrac: null, glassApex: false, fascia: 0.16, fasciaMat: null,
  capRound: true, capRings: 8, capSegments: 60,
  wallInset: 0.4, plinthH: 0.35, floorY: 0.4, posts: 0.12,
  entrance: null, vents: [], bed: null,
  snow: { depth: 0.3, overhang: 0.14, droop: 0.1 },
};

/** Builder with the units.js contract: { group, top, smoke }. Built once per parameter set (all cabins of a type
 *  share it; bake() copies the geometry per instance), so 27 cabins cost one build. */
const built = new Map();
export function glassKota(f, M, params = {}) {
  const key = JSON.stringify(params);
  let hit = built.get(key);
  if (!hit || hit.M !== M) { hit = { M, made: buildKota(M, params) }; built.set(key, hit); }
  return { ...hit.made, group: hit.made.group.clone() };
}
function buildKota(M, params) {
  const p = { ...KOTA_DEFAULTS, ...params, snow: params.snow === false ? false : { ...KOTA_DEFAULTS.snow, ...(params.snow || {}) } };
  const shingle = M.shingleDark || M.roofEdge, band = M.metalRoof || M.roofEdge, frame = M.metal || M.post;
  const S = new Soup();

  // ---- plan: full convex eave polygon. Edge 0 = front, edges 1..n-1 = +z side, n = rear, n+1.. = -z side.
  const h = p.plan;
  const P = [[h[0][0], -h[0][1]], ...h, ...h.slice(1).reverse().map(([x, z]) => [x, -z])];
  const s = area2(P) > 0 ? 1 : -1;
  const E = P.map((a, i) => {
    const b = P[(i + 1) % P.length], dx = b[0] - a[0], dz = b[1] - a[1], len = Math.hypot(dx, dz);
    const nx = (-dz / len) * s, nz = (dx / len) * s; // inward
    return { a, b, nx, nz, c: -(nx * a[0] + nz * a[1]), t: [dx / len, dz / len] };
  });
  const isGlass = (i) => i === 0 || (i >= 1 && i <= p.glassFacets) || (i >= P.length - p.glassFacets);
  const dist = (i, q) => E[i].nx * q[0] + E[i].nz * q[1] + E[i].c;

  // ---- roof surface. Every eave edge carries its own profile (a list of planes by distance from that edge):
  // glazed facets = the flatter metal band, then the glass pitch; shingled facets = ONE plane from the eave edge to
  // the ridge (photos: the metal band exists only under the glass). The roof is the lower envelope of the facets,
  // so each facet piece is the plan region where it is lowest; pieces are cut by half-planes only (exact, shared
  // edges). Above the glass top the front is a lofted round cap; the rear keeps its straight hips into the gable.
  let inR = 0;
  E.forEach((ei, i) => { // inradius (ridge distance) of the plain polygon
    let r = P;
    for (let j = 0; j < E.length && r; j++) if (j !== i) r = clip(r, E[j].nx - ei.nx, E[j].nz - ei.nz, E[j].c - ei.c);
    if (r) r.forEach((q) => { inR = Math.max(inR, dist(i, q)); });
  });
  // WS-61 (owner 2026-10-15: "all five windows are the same size"): glassApex = the glazed facets share the pitch
  // that makes them meet in ONE apex on the ridge line at ridgeY (a true half-pyramid: equal eave edges give equal
  // panes). The apex is the least-squares centre of the glazed edges on the axis z = 0. The glass then owns exactly
  // the cone from the apex through the two glass/shingle eave corners (straight hips, photos), and only facets of
  // the same kind compete inside / outside it: the flatter metal band would otherwise spill round the corner onto
  // the shingled side and drag the outer panes along. The shingle sits a little higher than the band at that hip;
  // a flashing face closes the step (photos: a raised metal strip runs up that hip). Without glassApex the glass
  // pitch comes from the whole plan's inradius and the outer panes run on past the others.
  const fit = p.glassApex ? glassApexFit(E.filter((_, i) => isGlass(i))) : null;
  const rG = fit ? fit.r : inR;
  let cone = null; // [[nx, nz, c] x2]: both >= 0 = the glazed cone
  if (fit) {
    const ap = [fit.x, 0], fr = [(E[0].a[0] + E[0].b[0]) / 2, (E[0].a[1] + E[0].b[1]) / 2];
    cone = [E[p.glassFacets].b, E[P.length - p.glassFacets].a].map((C) => {
      const nx = -(C[1] - ap[1]), nz = C[0] - ap[0], c = -(nx * ap[0] + nz * ap[1]), sg = nx * fr[0] + nz * fr[1] + c > 0 ? 1 : -1;
      return [nx * sg, nz * sg, c * sg];
    });
  }
  const inCone = (q) => !!cone && cone.every((h) => h[0] * q[0] + h[1] * q[1] + h[2] >= -1e-6);
  const kindParts = (poly, glassy) => { // plan pieces of `poly` a facet kind may own (convex)
    if (!cone || !poly) return [poly];
    const [h1, h2] = cone;
    if (glassy) { const q = clip(poly, h1[0], h1[1], h1[2]); return [q && clip(q, h2[0], h2[1], h2[2])]; }
    const q2 = clip(poly, h1[0], h1[1], h1[2]);
    return [clip(poly, -h1[0], -h1[1], -h1[2]), q2 && clip(q2, -h2[0], -h2[1], -h2[2])];
  };
  const tA = Math.tan(p.bandPitch * D2R), yBand = p.band * tA;
  const tB = (p.ridgeY - p.eaveY - yBand) / Math.max(rG - p.band, 0.1); // glass pitch
  const tO = (p.ridgeY - p.eaveY) / inR; // shingled facets: one plane eave -> ridge
  const prof = E.map((_, i) => (isGlass(i)
    ? [{ lo: 0, hi: p.band, y0: 0, k: tA, band: true }, { lo: p.band, hi: Infinity, y0: yBand, k: tB }]
    : [{ lo: 0, hi: Infinity, y0: 0, k: tO }]));
  const lin = (i, pc) => { const k = pc.k; return [k * E[i].nx, k * E[i].nz, p.eaveY + pc.y0 + k * (E[i].c - pc.lo)]; }; // y = ax x + az z + a0
  const Hof = (L, q) => L[0] * q[0] + L[1] * q[1] + L[2];
  const finvOf = (i, y) => { for (const pc of prof[i]) { const d = pc.lo + (y - p.eaveY - pc.y0) / pc.k; if (d <= pc.hi + 1e-9) return d; } return Infinity; };
  const levelOf = (y, extra, keep) => { let r = P; for (let i = 0; i < E.length && r; i++) if (keep(i)) r = clip(r, E[i].nx, E[i].nz, E[i].c - finvOf(i, y) - extra); return r; };
  const levelPoly = (y, extra = 0) => levelOf(y, extra, () => true);
  const levelSlots = (y, extra = 0) => (cone // WS-61 glassApex: glass-level inside the cone + shingle-level outside it (fixed slots)
    ? [...kindParts(levelOf(y, extra, isGlass), true), ...kindParts(levelOf(y, extra, (i) => !isGlass(i)), false)]
    : [levelPoly(y, extra)]);
  const levelParts = (y, extra = 0) => levelSlots(y, extra).filter(Boolean);
  const yTop = p.glassTopFrac != null ? p.eaveY + yBand + p.glassTopFrac * (p.ridgeY - p.eaveY - yBand) : p.ridgeY - p.glassCap;

  const regions = []; // { i, pc, L, poly }
  E.forEach((ei, i) => prof[i].forEach((pc) => {
    const L = lin(i, pc);
    let list = kindParts(clip(P, ei.nx, ei.nz, ei.c - pc.lo), isGlass(i)); // WS-61: the whole plan unless glassApex
    if (pc.hi < Infinity) list = list.map((q) => q && clip(q, -ei.nx, -ei.nz, pc.hi - ei.c));
    for (let j = 0; j < E.length; j++) {
      if (j === i || (cone && isGlass(j) !== isGlass(i))) continue;
      const next = [];
      for (const poly of list) {
        if (!poly) continue;
        for (const pj of prof[j]) {
          let q = clip(poly, E[j].nx, E[j].nz, E[j].c - pj.lo);
          if (q && pj.hi < Infinity) q = clip(q, -E[j].nx, -E[j].nz, pj.hi - E[j].c);
          const Lj = lin(j, pj);
          if (q) q = clip(q, Lj[0] - L[0], Lj[1] - L[1], Lj[2] - L[2]); // this piece is the lowest
          if (q) next.push(q);
        }
      }
      list = next;
    }
    for (const poly of list) regions.push({ i, pc, L, poly });
  }));
  const owner = (q) => { // the roof piece over a plan point (lowest plane among the pieces whose range holds it)
    let best = null, by = Infinity;
    const g = inCone(q);
    for (const r of regions) { const d = dist(r.i, q); if (d < r.pc.lo - 1e-6 || d > r.pc.hi + 1e-6 || (cone && isGlass(r.i) !== g)) continue; const y = Hof(r.L, q); if (y < by) { by = y; best = r; } }
    return best;
  };
  const inside = (q) => E.every((_, i) => dist(i, q) > 1e-6);

  // ridge segment (front end B) and the cut x between the round front cap and the straight-hipped rear
  const QR = levelParts(p.ridgeY - 0.03 * tO).flat();
  if (!QR.length) QR.push(...P);
  let A = QR[0], B = QR[0], far = -1;
  for (const a of QR) for (const b of QR) { const dd = Math.hypot(a[0] - b[0], a[1] - b[1]); if (dd > far) { far = dd; A = a; B = b; } }
  if (B[0] < A[0]) [A, B] = [B, A];
  A = [A[0], 0]; B = [B[0], 0];
  const xc = p.capRound ? (A[0] + B[0]) / 2 : Infinity;

  const pieces = []; // { r, poly, kind: 'band'|'glass'|'shingle', cap }
  for (const r of regions) {
    const below = clip(r.poly, -r.L[0], -r.L[1], yTop - r.L[2]);
    const above = clip(r.poly, r.L[0], r.L[1], r.L[2] - yTop);
    if (below) pieces.push({ r, poly: below, kind: r.pc.band ? 'band' : isGlass(r.i) ? 'glass' : 'shingle' });
    const rear = above && clip(above, -1, 0, xc); // x <= xc: straight hips; the front is the lofted cap
    if (rear) pieces.push({ r, poly: rear, kind: 'shingle', cap: true });
  }
  // WS-61: with a modelled room inside (insideModel), the main roof has a hole where the entrance roof runs into it (a
  // dormer): from outside it lies under the entrance roof and is never seen, but inside it would cut through the room
  const dorm = p.insideModel && p.entrance ? dormer(p.entrance) : null;
  if (dorm) {
    const keep = pieces.flatMap((pc) => [1, -1].reduce((list, sgn) => list.flatMap((poly) => subtract(poly, dorm.under(pc.r.L, sgn))), [pc.poly]).map((poly) => ({ ...pc, poly })));
    pieces.length = 0; pieces.push(...keep);
  }
  const lift = (q, r, dy = 0) => [q[0], Hof(r.L, q) + dy, q[1]];
  const roofUV = (r) => (q) => [(q[0] * E[r.i].t[0] + q[2] * E[r.i].t[1]) * 0.5, (dist(r.i, [q[0], q[2]]) / Math.cos(Math.atan(r.pc.k))) * 0.5];
  for (const pc of pieces) {
    const mat = pc.kind === 'band' ? band : pc.kind === 'glass' ? M.glass : shingle;
    S.poly(mat, pc.poly.map((q) => lift(q, pc.r)), 'up', mat === shingle || mat.userData.slopeUV ? roofUV(pc.r) : null); // WS-37: metal band seams run up the slope
  }

  // WS-61 glassApex: the step between the band/glass and the higher shingle along each glass/shingle hip, closed by a
  // flashing face from the eave corner up to the glass top (above it the cap loft runs over the step).
  if (cone) {
    const gi = [p.glassFacets, P.length - p.glassFacets], si = [p.glassFacets + 1, P.length - p.glassFacets - 1];
    cone.forEach((h, k) => {
      const C = k ? E[gi[1]].a : E[gi[0]].b, ap = [fit.x, 0];
      const at = (t) => [C[0] + (ap[0] - C[0]) * t, C[1] + (ap[1] - C[1]) * t];
      const yG = (t) => { const d = dist(gi[k], at(t)); return d <= p.band ? p.eaveY + tA * d : p.eaveY + yBand + tB * (d - p.band); };
      const yS = (t) => p.eaveY + tO * dist(si[k], at(t));
      const solve = (f) => { let lo = 0, hi = 1; for (let it = 0; it < 50; it++) { const m = (lo + hi) / 2; if (f(m) < yTop) lo = m; else hi = m; } return lo; };
      const tb = Math.min(1, p.band / Math.max(dist(gi[k], ap), 1e-6)), tg = solve(yG), ts = solve(yS);
      const pt = (t, y) => { const q = at(t); return [q[0], y, q[1]]; };
      const face = [pt(0, p.eaveY), ...(tb < tg ? [pt(tb, yG(tb))] : []), pt(tg, yTop), pt(ts, yTop)];
      if (tg > ts + 1e-4) S.poly(frame, face, [h[0], 0, h[1]]);
    });
  }

  // ---- round cap over the glass (front of xc): rings = level polygons of the roof, rounded off more and more
  // towards the ridge; ring 0 is the exact level polygon at the glass top, the end rays sit on x = xc, so the cap
  // closes onto the pane tops, the side planes and the straight-hipped rear without a gap or a step.
  const cap = p.capRound ? capLoft() : null;
  if (cap) {
    loftEmit(cap, (q) => q, shingle, 'smooth');
    const coarse = cap.filter((_, k) => k % 3 === 0 || k === cap.length - 1).map((rr) => rr.filter((_, j) => j % 3 === 0 || j === rr.length - 1));
    if (!p.insideModel) loftEmit(coarse, (q) => [q[0], q[1] - 0.15, q[2]], M.interior, null); // ceiling seen through the glass (coarse: only glimpsed); WS-61: not under a modelled room
  }
  function capLoft() {
    const nr = [0, 1], L = B[0] - xc, per = 2 * L + Math.PI, N = p.capSegments;
    const rays = [];
    for (let j = 0; j <= N; j++) { // open strip: -z side at xc -> around the front end B -> +z side at xc
      const sp = (j / N) * per;
      let O, u;
      if (sp <= L) { O = [xc + sp, 0]; u = [0, -1]; }
      else if (sp < L + Math.PI) { const a = sp - L; O = B; u = [Math.sin(a), -Math.cos(a)]; }
      else { O = [B[0] - (sp - L - Math.PI), 0]; u = nr; }
      rays.push({ O, u });
    }
    const R0 = levelParts(yTop).flat();
    if (!R0.length) R0.push(...P);
    // WS-61 glassApex: ring 0 steps across each hip (glass top corner, then the higher shingle's corner, both on the
    // hip line through the apex, so on one ray): the glass corner's ray is turned a hair into the cone to keep both
    const G0 = cone ? kindParts(levelOf(yTop, 0, isGlass), true)[0] || [] : [];
    for (const c of R0) { // rays through the ring-0 corners in front of xc (pane tops, hips get a vertex)
      if (c[0] <= xc + 1e-6) continue;
      const O = [Math.max(xc, Math.min(B[0], c[0])), 0], len = Math.hypot(c[0] - O[0], c[1] - O[1]);
      if (len <= 1e-6) continue;
      let u = [(c[0] - O[0]) / len, (c[1] - O[1]) / len];
      const h = G0.some((g) => Math.hypot(g[0] - c[0], g[1] - c[1]) < 1e-9) && cone.find((hh) => Math.abs(hh[0] * c[0] + hh[1] * c[1] + hh[2]) < 1e-6 * Math.hypot(hh[0], hh[1]));
      if (h) { const hn = Math.hypot(h[0], h[1]), v = [u[0] + (h[0] / hn) * 1e-4, u[1] + (h[1] / hn) * 1e-4], l = Math.hypot(...v); u = [v[0] / l, v[1] / l]; }
      rays.push({ O, u });
    }
    const key = (r) => { // position along the open strip
      if (r.O[0] < B[0] - 1e-9 && Math.abs(r.u[0]) < 1e-9) return r.u[1] < 0 ? r.O[0] - xc : 2 * L + Math.PI - (r.O[0] - xc);
      return L + Math.atan2(r.u[0], -r.u[1]);
    };
    rays.sort((a, b) => key(a) - key(b));
    const uniq = rays.filter((r, k) => k === 0 || key(r) - key(rays[k - 1]) > 1e-6);
    const rings = [];
    for (let k = 0; k <= p.capRings; k++) {
      const sk = k / p.capRings, y = yTop + (p.ridgeY - yTop) * sk;
      if (k === p.capRings) { rings.push(uniq.map(({ O }) => [O[0], p.ridgeY, O[1]])); break; }
      const room = (p.ridgeY - y) / tO, r = Math.max(0, Math.min(room * Math.min(1, 2 * sk) * 0.97, room - 0.03));
      // WS-61: a part too small to round at this height keeps its exact level line (the cap then continues the facet
      // planes, flush with the glass) instead of the whole level polygon grown by r, which bulged out over the panes
      const Qr = levelSlots(y, r), Q0 = levelSlots(y);
      const isIn = (q) => Qr.some((pr, i) => (pr ? distConvex(q, pr) <= r : !!Q0[i] && distConvex(q, Q0[i]) <= 0));
      rings.push(uniq.map(({ O, u }) => {
        let lo = 0, hi = 12;
        for (let it = 0; it < 50; it++) { const mid = (lo + hi) / 2; if (isIn([O[0] + u[0] * mid, O[1] + u[1] * mid])) lo = mid; else hi = mid; }
        return [O[0] + u[0] * lo, y, O[1] + u[1] * lo];
      }));
    }
    return rings;
  }
  function loftEmit(rings, map, mat, grp) {
    for (let k = 0; k < rings.length - 1; k++) {
      const a = rings[k], b = rings[k + 1];
      for (let j = 0; j < a.length - 1; j++) {
        for (const tri of [[a[j], a[j + 1], b[j + 1]], [a[j], b[j + 1], b[j]]]) {
          if (Math.hypot(...newell(tri)) > 1e-9) S.poly(mat, tri.map(map), 'up', null, grp);
        }
      }
    }
  }

  // glass frames: every pane edge once. Edges inside one pane (cuts by a neighbour's band line) get no bar; a hip
  // between two panes is drawn by the lower facet index only.
  const cen = (poly) => poly.reduce((s2, q) => [s2[0] + q[0] / poly.length, s2[1] + q[1] / poly.length], [0, 0]);
  const probe = (a, b, c0, eps = 0.02) => { const m = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], v = [m[0] - c0[0], m[1] - c0[1]], l = Math.hypot(...v) || 1; return [m[0] + (v[0] / l) * eps, m[1] + (v[1] / l) * eps]; };
  for (const pc of pieces) {
    if (pc.kind !== 'glass') continue;
    const c0 = cen(pc.poly);
    pc.poly.forEach((q, k) => {
      const w = pc.poly[(k + 1) % pc.poly.length], pr = probe(q, w, c0), o = inside(pr) ? owner(pr) : null;
      const yPr = o ? Hof(o.L, pr) : 0;
      const other = o && o.i !== pc.r.i && isGlass(o.i) && !o.pc.band && yPr < yTop;
      if (o && o.i === pc.r.i && !o.pc.band && yPr < yTop) return; // same pane
      if (other && o.i < pc.r.i) return; // the neighbouring pane draws the shared hip
      S.add(frame, rodGeo(lift(q, pc.r, 0.015), lift(w, pc.r, 0.015), 0.045));
    });
  }

  // ---- fascia, soffit, walls, plinth, corner posts
  const ys = p.eaveY - p.fascia;
  const fasciaM = (p.fasciaMat && M[p.fasciaMat]) || band; // WS-61: the board under the metal band's edge (default: the band's own metal)
  const W = offset(P, p.wallInset);
  const cx = P.reduce((a, q) => a + q[0], 0) / P.length;
  const out2 = (a, b) => { const mx = (a[0] + b[0]) / 2 - cx, mz = (a[1] + b[1]) / 2; return [mx, 0, mz]; };
  for (let i = 0; i < P.length; i++) {
    const a = P[i], b = P[(i + 1) % P.length], wa = W[i], wb = W[(i + 1) % W.length], o = out2(a, b);
    if (dorm && Math.abs(a[0] - b[0]) < 1e-6 && a[0] < 0) continue; // WS-61: the rear edge is inside the vestibule (the room's door is there)
    S.quad(fasciaM, [a[0], p.eaveY, a[1]], [b[0], p.eaveY, b[1]], [b[0], ys, b[1]], [a[0], ys, a[1]], o);
    S.quad(M.wood, [a[0], ys, a[1]], [b[0], ys, b[1]], [wb[0], ys, wb[1]], [wa[0], ys, wa[1]], [0, -1, 0]);
    S.quad(M.wood, [wa[0], p.plinthH, wa[1]], [wb[0], p.plinthH, wb[1]], [wb[0], ys, wb[1]], [wa[0], ys, wa[1]], o);
    S.quad(M.plinth, [wa[0], -0.6, wa[1]], [wb[0], -0.6, wb[1]], [wb[0], p.plinthH, wb[1]], [wa[0], p.plinthH, wa[1]], o);
    if (p.posts && !(p.entrance && Math.abs(wa[1]) < p.entrance.wallHalf && wa[0] < p.entrance.frontX)) {
      const prev = W[(i - 1 + W.length) % W.length]; // square post on the corner, faces along the corner bisector
      S.add(M.post, boxGeo(wa[0], (p.plinthH + ys) / 2, wa[1], p.posts, ys - p.plinthH, p.posts, -Math.atan2(wb[1] - prev[1], wb[0] - prev[0])));
    }
  }

  // ---- interior seen through the glass: floor, inner wall ring, ceiling under the opaque roof, bed. WS-61:
  // params.insideModel = a modelled room is drawn there instead (walkin.js: its low-poly copy behind every unit's glass)
  if (!p.insideModel) {
    const I = offset(P, Math.max(p.band, p.wallInset + 0.08)); // WS-61: always inside the walls (a narrow band would put it outside)
    const yi = p.eaveY + yBand - 0.1;
    S.poly(M.interior, I.map(([x, z]) => [x, p.floorY, z]), 'up');
    for (let i = 0; i < I.length; i++) {
      const a = I[i], b = I[(i + 1) % I.length];
      S.quad(M.interior, [a[0], p.floorY, a[1]], [b[0], p.floorY, b[1]], [b[0], yi, b[1]], [a[0], yi, a[1]], out2(a, b).map((v) => -v));
    }
    for (const pc of pieces) { // ceiling under the shingles, inside the walls (seen through the glass)
      const q = pc.kind === 'shingle' && clip(pc.poly, E[pc.r.i].nx, E[pc.r.i].nz, E[pc.r.i].c - Math.max(p.band, p.wallInset + 0.08));
      if (q) S.poly(M.interior, q.map((v) => lift(v, pc.r, -0.12)), [0, -1, 0]);
    }
    if (p.bed) {
      const { x0, x1, w, top, head = 0.5 } = p.bed;
      S.add(M.linen, boxGeo((x0 + x1) / 2, (p.floorY + top) / 2, 0, x1 - x0, top - p.floorY, w));
      S.add(M.wood, boxGeo(x0 - 0.05, (p.floorY + top + head) / 2, 0, 0.1, top + head - p.floorY, w + 0.1));
    }
  } // WS-61 insideModel

  // ---- entrance vestibule (gable): walls, gable roof prism, door, sign, lamps, landing + steps
  const en = p.entrance;
  let topY = p.ridgeY;
  if (en) {
    const { rearX: xr, frontX: xf, wallHalf: wh, roofHalf: rh, eaveY: ey, ridgeY: ry, overhang: ov = 0.3, roofEndX = 0, roofT = 0.15 } = en;
    const slope = (ry - ey) / rh, yAt = (z) => ry - Math.abs(z) * slope; // roof top surface
    const yw = yAt(wh) - roofT / Math.cos(Math.atan(slope)); // wall top under the roof
    const gable = [[-wh, p.plinthH], [wh, p.plinthH], [wh, yw], [0, ry - roofT / Math.cos(Math.atan(slope))], [-wh, yw]];
    const gab = (x) => gable.map(([z, y]) => [x, y, z]);
    // WS-61: with a modelled room inside (insideModel), nothing of the vestibule may stand inside the main walls: its
    // front gable, the side walls' ends and the roof prism's trim and underside stop at the main walls (from outside
    // they are under the main roof either way; inside they would block the room's view)
    const xMain = p.insideModel ? wallXAt(W, wh) : Infinity, xfw = Math.min(xf, xMain);
    S.poly(M.wood, gab(xr), [-1, 0, 0]);
    if (!p.insideModel) S.poly(M.wood, gab(xf), [1, 0, 0]);
    for (const sz of [-1, 1]) {
      S.quad(M.wood, [xr, p.plinthH, sz * wh], [xfw, p.plinthH, sz * wh], [xfw, yw, sz * wh], [xr, yw, sz * wh], [0, 0, sz]);
      S.quad(M.plinth, [xr, -0.6, sz * wh], [xfw, -0.6, sz * wh], [xfw, p.plinthH, sz * wh], [xr, p.plinthH, sz * wh], [0, 0, sz]);
      if (p.posts) S.add(M.post, boxGeo(xr + p.posts / 2 - 0.02, (p.plinthH + yw) / 2, sz * (wh - p.posts / 2 + 0.02), p.posts, yw - p.plinthH, p.posts));
    }
    S.quad(M.plinth, [xr, -0.6, -wh], [xr, -0.6, wh], [xr, p.plinthH, wh], [xr, p.plinthH, -wh], [-1, 0, 0]);
    // roof: closed hexagonal prism along x (top = shingles, the rest dark trim)
    const x0 = xr - ov, x1 = roofEndX, dyT = roofT / Math.cos(Math.atan(slope));
    const sec = [[-rh, yAt(rh)], [0, ry], [rh, yAt(rh)], [rh, yAt(rh) - dyT], [0, ry - dyT], [-rh, yAt(rh) - dyT]];
    const at = (x, k) => [x, sec[k][1], sec[k][0]];
    const vUV = (q) => [q[0] * 0.5, (Math.abs(q[2]) / Math.cos(Math.atan(slope))) * 0.5];
    if (dorm) { // WS-61: only where it stands above the main roof (below it, it would hang inside the room)
      const mains = E.map((_, i) => i).filter((i) => !isGlass(i)).map((i) => lin(i, prof[i][0]));
      for (const sg of [1, -1]) {
        const rect = [[x0, 0], [x1, 0], [x1, sg * rh], [x0, sg * rh]];
        const under = mains.map((L) => [L[0], L[1] + slope * sg, L[2] - ry]); // main roof above the prism plane
        for (const q of subtract(rect, under)) S.poly(shingle, q.map(([x, z]) => [x, ry - slope * sg * z, z]), 'up', vUV);
      }
    } else {
      S.quad(shingle, at(x0, 0), at(x1, 0), at(x1, 1), at(x0, 1), 'up', vUV);
      S.quad(shingle, at(x0, 1), at(x1, 1), at(x1, 2), at(x0, 2), 'up', vUV);
    }
    const xt = Math.min(x1, wallXAt(W, 0) + (p.insideModel ? 0 : Infinity)); // WS-61: trim and underside end at the main wall
    S.quad(band, at(x0, 2), at(xt, 2), at(xt, 3), at(x0, 3), [0, 0, 1]);
    S.quad(band, at(x0, 5), at(xt, 5), at(xt, 0), at(x0, 0), [0, 0, -1]);
    S.quad(M.wood, at(x0, 3), at(xt, 3), at(xt, 4), at(x0, 4), [0, -1, 0]);
    S.quad(M.wood, at(x0, 4), at(xt, 4), at(xt, 5), at(x0, 5), [0, -1, 0]);
    for (const [xx, w] of p.insideModel ? [[x0, -1]] : [[x0, -1], [x1, 1]]) { // verge ends: two quads (the chevron section is not convex)
      S.quad(band, at(xx, 0), at(xx, 1), at(xx, 4), at(xx, 5), [w, 0, 0]);
      S.quad(band, at(xx, 1), at(xx, 2), at(xx, 3), at(xx, 4), [w, 0, 0]);
    }
    // door, name board, wall lamps (rear face = -x)
    const d = en.door || { w: 0.9, h: 2.0 };
    S.add((d.mat && M[d.mat]) || M.post, boxGeo(xr - 0.03, p.floorY + d.h / 2, 0, 0.06, d.h, d.w));
    if (en.sign) S.add(M.pine || M.wood, boxGeo(xr - 0.025, en.sign.y, 0, 0.05, en.sign.h, en.sign.w));
    for (const [lz, ly, lw, lh] of en.lamps || []) S.add(M.lamp, boxGeo(xr - 0.04, ly, lz, 0.05, lh, lw));
    if (en.led) { // LED strip under the hood: along the underside of both sloped rakes of the overhang at the door end
      const r = en.led.r ?? 0.012, xl = x0 + (en.led.inset ?? 0.1), zEnd = rh - (en.led.endGap ?? 0.12);
      const yU = (z) => yAt(z) - dyT - r; // just under the roof's underside
      for (const s of [-1, 1]) S.add(M.led || M.lamp, rodGeo([xl, yU(zEnd), s * zEnd], [xl, yU(0), 0], r, 6));
      if (en.led.wash) { // soft downward wash on the gable face below the rakes (additive, follows the LED night ramp)
        const h = en.led.wash, xw = xr - 0.004, yTop = (z) => yAt(z) - dyT;
        const uvf = (q) => [0.5, Math.min(0.999, Math.max(0.001, (yTop(q[2]) - q[1]) / h))];
        for (const s of [-1, 1]) S.poly(ledWash(M), [[xw, yTop(0), 0], [xw, yTop(wh), s * wh], [xw, yTop(wh) - h, s * wh], [xw, yTop(0) - h, 0]], [-1, 0, 0], uvf);
      }
    }
    let sx = xr;
    for (const [dep, hgt, wid] of en.steps || []) { S.add(M.deck, boxGeo(sx - dep / 2, (hgt - 0.6) / 2, 0, dep, hgt + 0.6, wid)); sx -= dep; }
    topY = Math.max(topY, ry);
    if (p.snow) vestibuleSnow(S, M, { x0, x1, xl: xt, sec, slope, rh, ry, yAt }, p.snow);
  }

  // ---- roof vents (photos only)
  for (const v of p.vents || []) {
    if (v.enabled === false) continue; // unconfirmed pipe: data only
    const ow = dorm && inside([v.x, v.z]) ? owner([v.x, v.z]) : null; // WS-61: with a room inside, the foot ends at the roof
    const yb = ow ? Hof(ow.L, [v.x, v.z]) - 0.03 : v.base ?? v.top - 1.4; // foot hidden inside the roof
    S.add(M.post, new THREE.CylinderGeometry(v.r || 0.08, v.r || 0.08, v.top - yb, 10).translate(v.x, (v.top + yb) / 2, v.z));
    if (v.cap) S.add(M.post, new THREE.CylinderGeometry(v.cap, v.cap, 0.08, 10).translate(v.x, v.top + 0.04, v.z));
  }

  // ---- snow layer (separate part): the opaque facets + the eave band lifted by `depth`, an overhanging lip at
  // the eave and a side wall where the snow stops at the glass. Smooth normals make it read as a soft cushion.
  if (p.snow) mainSnow(S, M, { P, E, pieces, lift, dist, eaveY: p.eaveY, cap, owner, inside, yTop, Hof, isGlass, loftEmit, probe, cen, xc, B, cone }, p.snow);

  const group = S.build({ smooth: new Set([M.snow]) });
  return { group, top: topY + (p.snow ? p.snow.depth : 0) + 0.2, smoke: [] };
}

function mainSnow(S, M, ctx, sn) {
  const { P, E, pieces, lift, dist, eaveY, cap, owner, inside, yTop, Hof, isGlass, loftEmit, probe, cen, xc, B, cone } = ctx;
  const c = sn.depth, o = sn.overhang;
  const out = offset(P, -o), outL = offset(P, -o * 0.75);
  const onEdges = (q) => E.map((_, i) => i).filter((i) => Math.abs(dist(i, q)) < 1e-6);
  const eaveOut = (q, f) => { // pushed out over the eave: corners along the miter, edge points along the normal
    const k = P.findIndex((v) => Math.hypot(v[0] - q[0], v[1] - q[1]) < 1e-5);
    if (k >= 0) return (f === 1 ? out : outL)[k];
    const i = onEdges(q)[0];
    return [q[0] - E[i].nx * o * f, q[1] - E[i].nz * o * f];
  };
  const top = (q, r) => {
    if (!onEdges(q).length) return lift(q, r, c);
    const e2 = eaveOut(q, 1);
    return [e2[0], eaveY + c - sn.droop, e2[1]];
  };
  const atGlass = (pr) => { if (!inside(pr)) return false; const ow = owner(pr); return !!ow && isGlass(ow.i) && !ow.pc.band && Hof(ow.L, pr) < yTop - 1e-6; };
  for (const pc of pieces) {
    if (pc.kind === 'glass') continue;
    S.poly(M.snow, pc.poly.map((q) => top(q, pc.r)), 'up');
    const c0 = cen(pc.poly);
    pc.poly.forEach((a, k) => {
      const b = pc.poly[(k + 1) % pc.poly.length], ea = onEdges(a), eb = onEdges(b);
      const mid = [(a[0] + b[0]) / 2 - c0[0], 0, (a[1] + b[1]) / 2 - c0[1]];
      if (ea.some((i) => eb.includes(i))) { // eave lip: outer face down below the eave, underside back to the fascia
        const lo = (q) => { const e2 = eaveOut(q, 0.75); return [e2[0], eaveY - sn.droop - 0.04, e2[1]]; };
        S.quad(M.snow, top(a, pc.r), top(b, pc.r), lo(b), lo(a), mid);
        S.quad(M.snow, lo(a), lo(b), [b[0], eaveY - 0.01, b[1]], [a[0], eaveY - 0.01, a[1]], [0, -1, 0]);
      } else if (atGlass(probe(a, b, c0))) { // stops at a pane: a short wall down to the roof
        S.quad(M.snow, top(a, pc.r), top(b, pc.r), lift(b, pc.r, 0.005), lift(a, pc.r, 0.005), mid);
      }
    });
  }
  if (!cap) return;
  // the cap's snow: the lofted cap lifted by `depth`; a short wall where ring 0 runs along a pane top
  loftEmit(cap, (q) => [q[0], q[1] + c, q[2]], M.snow, null);
  const r0 = cap[0];
  for (let j = 0; j < r0.length - 1; j++) {
    const q = r0[j], w = r0[j + 1], m2 = [(q[0] + w[0]) / 2, (q[2] + w[2]) / 2];
    const ax = [Math.max(xc, Math.min(B[0], m2[0])), 0], v = [m2[0] - ax[0], m2[1] - ax[1]], l = Math.hypot(...v) || 1;
    const hip = cone && cone.find((h) => [q, w].every((u) => Math.abs(h[0] * u[0] + h[1] * u[2] + h[2]) < 1e-4)); // WS-61: ring 0 steps across a glassApex hip
    if (!hip && !atGlass([m2[0] + (v[0] / l) * 0.03, m2[1] + (v[1] / l) * 0.03])) continue;
    S.quad(M.snow, [q[0], q[1] + c, q[2]], [w[0], w[1] + c, w[2]], [w[0], w[1] + 0.005, w[2]], [q[0], q[1] + 0.005, q[2]], hip ? [hip[0], 0, hip[1]] : [v[0], 0, v[1]]);
  }
}

/** A convex polygon minus the convex region where every half-plane [nx, nz, c] (nx x + nz z + c >= 0) holds:
 *  the rest as convex pieces. */
function subtract(poly, hps) {
  const out = [];
  let cur = poly;
  for (const [nx, nz, c] of hps) {
    const outside = clip(cur, -nx, -nz, -c);
    if (outside) out.push(outside);
    cur = clip(cur, nx, nz, c);
    if (!cur) break;
  }
  return out;
}
/** The entrance roof as a dormer: under(L, s) = the half-planes of the region, on side s (+1 / -1 of z), where a roof
 *  plane L (y = L0 x + L1 z + L2) lies under the entrance roof's top. */
function dormer(en) {
  const { roofHalf: rh, eaveY: ey, ridgeY: ry, roofEndX = 0 } = en, slope = (ry - ey) / rh;
  return { under: (L, sg) => [[-1, 0, roofEndX], [0, sg, 0], [0, -sg, rh], [-L[0], -slope * sg - L[1], ry - L[2]]] };
}

/** x of the main walls' rear face at lateral offset z (the polygon W, the rear half). */
function wallXAt(W, z) {
  let best = -Infinity;
  for (let i = 0; i < W.length; i++) {
    const a = W[i], b = W[(i + 1) % W.length];
    if (a[0] > 0 && b[0] > 0) continue;
    if ((a[1] - z) * (b[1] - z) > 0 || a[1] === b[1]) { if (a[1] === b[1] && a[1] === z) best = Math.max(best, Math.min(a[0], b[0])); continue; }
    const t = (z - a[1]) / (b[1] - a[1]), x = a[0] + (b[0] - a[0]) * t;
    if (x < 0) best = Math.max(best, x);
  }
  return best === -Infinity ? Infinity : best;
}

function vestibuleSnow(S, M, { x0, x1, xl = x1, slope, rh, ry, yAt }, sn) {
  const c = sn.depth * 0.9, o = sn.overhang, cosA = Math.cos(Math.atan(slope));
  const xa = x0 - o * 0.6;
  const eave = (sz, x) => [x, yAt(rh) + c - sn.droop, sz * (rh + o)];
  const ridge = (x) => [x, ry + c / cosA, 0];
  const lo = (sz, x) => [x, yAt(rh) - sn.droop - 0.04, sz * (rh + o * 0.75)];
  for (const sz of [-1, 1]) {
    S.quad(M.snow, eave(sz, xa), eave(sz, x1), ridge(x1), ridge(xa), 'up');
    S.quad(M.snow, eave(sz, xa), eave(sz, xl), lo(sz, xl), lo(sz, xa), [0, 0, sz]); // WS-61: lips end at the main wall (xl)
    S.quad(M.snow, lo(sz, xa), lo(sz, xl), [xl, yAt(rh) - 0.01, sz * rh], [xa, yAt(rh) - 0.01, sz * rh], [0, -1, 0]);
  }
  // verge end above the door and the overhang's underside (no roof under the snow beyond the verge)
  const u = (sz, x) => [x, yAt(rh) - 0.01, sz * rh], rr = (x) => [x, ry - 0.01, 0];
  S.quad(M.snow, eave(-1, xa), ridge(xa), rr(xa), u(-1, xa), [-1, 0, 0]);
  S.quad(M.snow, ridge(xa), eave(1, xa), u(1, xa), rr(xa), [-1, 0, 0]);
  for (const sz of [-1, 1]) {
    S.poly(M.snow, [eave(sz, xa), lo(sz, xa), u(sz, xa)], [-1, 0, 0]);
    S.quad(M.snow, u(sz, xa), u(sz, x0), rr(x0), rr(xa), [0, -1, 0]);
  }
}
